//legacy-code side menu context for managing collapse state
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

interface SideMenuContextType {
  isCollapsed: boolean;
  setCollapsed: (value: boolean) => void;
  toggleSideMenu: () => void;
}

const SideMenuContext = createContext<SideMenuContextType | undefined>(
  undefined,
);

interface SideMenuProviderProps {
  children: ReactNode;
  defaultCollapsed?: boolean;
  mobileBreakpoint?: number; // Default to 768px for mobile
}

export const SideMenuProvider = ({
  children,
  defaultCollapsed = false,
  mobileBreakpoint = 768,
}: SideMenuProviderProps) => {
  const getIsMobile = useCallback(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < mobileBreakpoint;
  }, [mobileBreakpoint]);

  const [isCollapsed, setIsCollapsedState] = useState<boolean>(() => {
    if (typeof window === "undefined") return defaultCollapsed;

    // If mobile, always start collapsed
    if (getIsMobile()) return true;

    return defaultCollapsed;
  });

  const [isMobile, setIsMobile] = useState(getIsMobile);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const currentIsMobile = getIsMobile();
      const wasMobile = isMobile;
      setIsMobile(currentIsMobile);

      if (currentIsMobile && !isCollapsed) {
        setIsCollapsedState(true);
      }

      // Close mobile menu if resizing to desktop
    };

    handleResize();

    let timeoutId: NodeJS.Timeout;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener("resize", debouncedResize);
    return () => {
      window.removeEventListener("resize", debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [isMobile, getIsMobile]);

  const setCollapsed = (value: boolean) => {
    setIsCollapsedState(value);
  };

  const toggleSideMenu = () => {
    setIsCollapsedState((prev) => !prev);
  };

  const value: SideMenuContextType = {
    isCollapsed,
    setCollapsed,
    toggleSideMenu,
  };

  return (
    <SideMenuContext.Provider value={value}>
      {children}
    </SideMenuContext.Provider>
  );
};

export const useSideMenu = (): SideMenuContextType => {
  const context = useContext(SideMenuContext);

  if (context === undefined) {
    throw new Error("useSideMenu must be used within a SideMenuProvider");
  }

  return context;
};
